import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import { newsList } from '@/mocks/news';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EmptyState from '@/components/feature/EmptyState';
import ShareDialog from '@/components/feature/ShareDialog';
import HotRanking from '@/pages/home/components/HotRanking';
import { formatDate, formatViews } from '@/utils/format';

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const article = useMemo(() => newsList.find((n) => n.id === id), [id]);
  const [shareOpen, setShareOpen] = useState(false);
  const [fontScale, setFontScale] = useState(1);

  usePageMeta({
    title: article ? `${article.title} - 靠逼` : '内容无法显示 - 靠逼',
    description: article ? article.excerpt : '内容无法显示，请稍后重试。',
    keywords: article ? article.tags.join(',') : '靠逼,娱乐资讯',
  });

  const related = useMemo(() => {
    if (!article) return [];
    const sameCat = newsList.filter((n) => n.category === article.category && n.id !== article.id);
    const pool = sameCat.length >= 3 ? sameCat : newsList.filter((n) => n.id !== article.id);
    return pool.slice(0, 3);
  }, [article]);

  const hot = useMemo(() => [...newsList].sort((a, b) => b.views - a.views).slice(0, 5), []);

  if (!article) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16 md:px-8">
        <EmptyState
          icon="ri-error-warning-line"
          title="内容无法显示，请稍后重试"
          description="你访问的资讯可能已被移除或链接有误。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mx-auto max-w-7xl px-4 py-6 md:px-8">
        <Breadcrumb items={[{ label: article.category, to: '/news' }, { label: '正文' }]} />
      </div>

      <div className="mx-auto max-w-7xl px-4 pb-8 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
          <article className="lg:col-span-2">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                {article.category}
              </span>
              {article.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
                  {t}
                </span>
              ))}
            </div>

            <h1 className="mt-4 font-heading text-2xl font-black leading-snug text-foreground-950 md:text-[34px]">
              {article.title}
            </h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-200 pb-4 text-xs text-foreground-500">
              <span className="flex items-center gap-1">
                <i className="ri-user-line" />
                {article.author}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-newspaper-line" />
                {article.source}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line" />
                {formatDate(article.publishedAt)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {formatViews(article.views)} 阅读
              </span>
              <div className="ml-auto flex items-center gap-2">
                <div className="flex items-center gap-1 rounded-full bg-background-100 p-0.5">
                  <button
                    type="button"
                    onClick={() => setFontScale((v) => Math.max(0.9, +(v - 0.1).toFixed(2)))}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-foreground-600 hover:bg-background-200"
                    aria-label="缩小字体"
                  >
                    <i className="ri-subtract-line" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setFontScale((v) => Math.min(1.4, +(v + 0.1).toFixed(2)))}
                    className="flex h-7 w-7 items-center justify-center rounded-full text-foreground-600 hover:bg-background-200"
                    aria-label="放大字体"
                  >
                    <i className="ri-add-line" />
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setShareOpen(true)}
                  className="flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-share-forward-line" />
                  分享
                </button>
              </div>
            </div>

            <div className="mt-6 overflow-hidden rounded-lg bg-background-100">
              <img
                src={article.cover}
                alt={article.title}
                title={`${article.title} - 靠逼`}
                className="h-full w-full object-cover"
              />
            </div>

            <div className="mt-6 leading-loose text-foreground-800" style={{ fontSize: `${fontScale}rem` }}>
              {article.content.map((p, i) => (
                <div key={i}>
                  <p className="mb-4">{p}</p>
                  {i === 1 && article.quote && (
                    <blockquote className="my-6 rounded-r-lg border-l-4 border-primary-500 bg-primary-50 px-5 py-4">
                      <p className="font-heading text-lg font-semibold text-foreground-900">
                        “{article.quote.text}”
                      </p>
                      <footer className="mt-2 text-xs text-foreground-500">—— {article.quote.source}</footer>
                    </blockquote>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8 rounded-lg border border-background-200 bg-background-100 p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-heading text-base font-bold text-foreground-950">相关资讯推荐</h3>
                <Link to="/news" className="text-xs text-foreground-500 hover:text-primary-600">
                  更多新闻
                </Link>
              </div>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/detail/${r.id}`}
                    className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
                  >
                    <div className="h-32 w-full overflow-hidden bg-background-100">
                      <img
                        src={r.cover}
                        alt={r.title}
                        title={`${r.title} - 靠逼`}
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <p className="line-clamp-2 p-3 text-sm font-medium text-foreground-800 transition-colors group-hover:text-primary-600">
                      {r.title}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          </article>

          <aside className="flex flex-col gap-6">
            <HotRanking items={hot} />
          </aside>
        </div>
      </div>

      <ShareDialog open={shareOpen} onClose={() => setShareOpen(false)} title={article.title} />
    </div>
  );
}